// src/pages/contentManagement/streaming/streamingList.js
import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { Divider, Spin } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import YoutubeCredentials from "./youtubeCreds";
import PlaylistConfig from "./playlistConfig";
import LivestreamConfig from "./livestreamConfig";
import "./streamingList.css";

// ─── Polling config ────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 15_000; //  15 seconds between each GET
const POLL_TIMEOUT_MS = 180_000; // 3 minutes before giving up

// ─── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  isLoading: true,
  youtube: null
};

// ─── Reducer ───────────────────────────────────────────────────────────────────
const streamingReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };

    case "setYoutube":
      return { ...state, youtube: action.payload };

    // Merges updated youtube config returned by any sub-component's save.
    // Keeps all existing fields intact — only overwrites what changed.
    case "updateYoutube":
      return { ...state, youtube: action.payload };

    default:
      return state;
  }
};

// ─── Component ─────────────────────────────────────────────────────────────────
function StreamingManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(streamingReducer, initialState);

  const { isLoading, youtube } = state;

  // Refs for polling — using refs avoids stale closure issues with setInterval
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isCredentialsConfigured = Boolean(youtube?.channelId);

  // ── Stop polling ───────────────────────────────────────────────────────────
  // Clears both the interval and the hard timeout. Safe to call multiple times.
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // ── Fetch youtube config ───────────────────────────────────────────────────
  // Returns the fetched youtube object so polling can inspect it directly.
  const fetchYoutubeSettings = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.YOUTUBE_SETTINGS, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const youtubeData = response.data.youtube;
      dispatch({ type: "setYoutube", payload: youtubeData });
      return youtubeData;
    } catch (error) {
      console.error("Error fetching YouTube settings:", error);

      if (error.response?.status === 404) {
        dispatch({ type: "setYoutube", payload: null });
      } else {
        showNotification("Failed to load YouTube settings. Please refresh and try again.", "error");
      }
      return null;
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // ── Start backfill polling ─────────────────────────────────────────────────
  // Called by YoutubeCredentials after a successful POST /youtube-settings/backfill.
  // Polls GET /youtube-settings every 15s until isBackfilling: false.
  // Hard timeout at 3 minutes — on timeout, silently PATCHes credentials to
  // reset isBackfilling: false in DynamoDB, then stops and notifies the admin.
  const startBackfillPolling = useCallback(() => {
    // Guard — don't start a second polling loop if one is already running
    if (pollingIntervalRef.current) return;

    console.log("Backfill polling started.");

    // ── Hard timeout — fires after 3 minutes ──────────────────────────────
    pollingTimeoutRef.current = setTimeout(async () => {
      stopPolling();
      console.warn("Backfill polling timed out after 3 minutes.");

      // Silently reset isBackfilling: false in DynamoDB via credentials PATCH
      // so the button re-enables even without a page refresh.
      try {
        const token = await getAuthToken();
        const channelId = state.youtube?.channelId;

        if (channelId) {
          await axios.patch(API_ENDPOINTS.YOUTUBE_SETTINGS_CREDENTIALS, { channelId }, { headers: { Authorization: `Bearer ${token}` } });
          // Update local state to re-enable the button
          dispatch({
            type: "updateYoutube",
            payload: { isBackfilling: false }
          });
          console.log("isBackfilling reset via credentials PATCH after timeout.");
        }
      } catch (resetError) {
        console.error("Failed to reset isBackfilling after timeout:", resetError);
      }

      showNotification(
        "YouTube history import is taking longer than expected. Please try again or contact support if the issue persists.",
        "warning",
        "Import Timeout"
      );
    }, POLL_TIMEOUT_MS);

    // ── Polling interval — fires every 15 seconds ──────────────────────────
    pollingIntervalRef.current = setInterval(async () => {
      console.log("Backfill poll — fetching youtube settings...");

      try {
        const token = await getAuthToken();
        const response = await axios.get(API_ENDPOINTS.YOUTUBE_SETTINGS, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const youtubeData = response.data.youtube;
        dispatch({ type: "setYoutube", payload: youtubeData });

        // Stop polling once the poller has finished
        if (youtubeData?.isBackfilling === false) {
          console.log("Backfill complete — stopping poll.");
          stopPolling();
          if (youtubeData?.backfillCompleted === true) {
            showNotification("YouTube history import completed successfully.", "success", "Import Complete");
          }
        }
      } catch (error) {
        // Network blip — log and continue polling, don't stop
        console.warn("Backfill poll fetch failed — will retry:", error.message);
      }
    }, POLL_INTERVAL_MS);
  }, [showNotification, stopPolling, state.youtube?.channelId]);

  // ── Handle save from any sub-component ────────────────────────────────────
  const handleSaveSuccess = useCallback((updatedYoutube) => {
    dispatch({ type: "updateYoutube", payload: updatedYoutube });
  }, []);

  // ── Mount ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchYoutubeSettings();
  }, [fetchYoutubeSettings]);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  // Prevents interval/timeout from firing after the component is gone.
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Sermons & Livestreams"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      {isLoading ? (
        <div className="strm-loading">
          <Spin size="large" />
        </div>
      ) : (
        <div className="strm-page">
          <YoutubeCredentials
            youtube={youtube}
            onSaveSuccess={handleSaveSuccess}
            onDeleteSuccess={fetchYoutubeSettings}
            showNotification={showNotification}
          />

          <Divider />
          <LivestreamConfig
            youtube={youtube}
            isCredentialsConfigured={isCredentialsConfigured}
            onBackfillStarted={startBackfillPolling}
            onSaveSuccess={handleSaveSuccess}
            showNotification={showNotification}
          />

          <Divider />
          <PlaylistConfig
            youtube={youtube}
            isCredentialsConfigured={isCredentialsConfigured}
            onSaveSuccess={handleSaveSuccess}
            showNotification={showNotification}
          />
        </div>
      )}
    </Layout>
  );
}

export default StreamingManagement;
